import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import store from "App/store/store";
import { TOAST_MESSAGE } from "App/store/constants";
import { v4 as uuidv4 } from "uuid";
import CryptoJS from "crypto-js";

// Type definitions
type ServerType = "login" | "taxi" | "payment";

interface ApiRequestParams {
  server: ServerType;
  url: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  data?: Record<string, any> | null;
  params?: Record<string, any>;
  headers?: Record<string, string>;
  successMsg?: boolean;
  errorMsg?: boolean;
  nextActionType: {
    PENDING: string;
    SUCCESS: string;
    FAILURE: string;
  };
}

// Retrieve settings from environment variables
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || "default_secure_key";
const ENABLE_ENCRYPTION = process.env.REACT_APP_ENABLE_ENCRYPTION === "true";

// Define API Base URLs
const urlList: Record<ServerType, string | undefined> = {
  login: process.env.REACT_APP_LOGIN_API_URL,
  taxi: process.env.REACT_APP_TAXI_API_URL,
  payment: process.env.REACT_APP_PAY_API_URL,
};

/**
 * Encrypts data using AES encryption.
 * @param {Record<string, any>} data - The data to encrypt.
 * @returns {string} - The encrypted data string.
 */
const encryptData = (data: Record<string, any>): string => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY).toString();
};

/**
 * Decrypts AES encrypted data.
 * @param {string} encryptedData - The encrypted string.
 * @returns {Record<string, any> | null} - The decrypted data.
 */
const decryptData = (encryptedData: string): Record<string, any> | null => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    return decryptedString ? JSON.parse(decryptedString) : null;
  } catch (error) {
    console.error("Decryption failed:", error);
    return null;
  }
};

/**
 * Secure API request handler with automatic encryption & decryption.
 */

export const MakeApiRequest = async ({
  server,
  url,
  method = "GET",
  data,
  params,
  headers,
  successMsg = false,
  errorMsg = false,
  nextActionType,
}: ApiRequestParams): Promise<any> => {
  try {
    if (!urlList[server]) throw new Error("Invalid server selection.");
    const baseURL = urlList[server] as string;

    // Retrieve authentication token from Redux store
    const storeData: any = store.getState();
    const token = storeData?.user?.token ? `Bearer ${storeData.user.token}` : "";

    // Device ID management
    let deviceId = localStorage.getItem("x-deviceid");
    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem("x-deviceid", deviceId);
    }

    // Ensure proper encryption structure
    let encryptedData: {} | null = null;
    let isEncrypted = ENABLE_ENCRYPTION;

    if (data && isEncrypted) {
      encryptedData = { encrypted: encryptData(data) };
    }

    // Secure headers
    const headersData: Record<string, string> = {
      "Content-Type": "application/json",
      "X-Device-ID": deviceId,
      Authorization: token,
      "X-Encrypted": isEncrypted ? "true" : "false",
      "X-Frame-Options": "DENY",
      "Content-Security-Policy": "default-src 'self'",
    };

    // Merge additional headers
    if (headers) {
      Object.entries(headers).forEach(([key, value]) => {
        headersData[key] = value;
      });
    }
    

    // Axios request options
    const options: AxiosRequestConfig = {
      baseURL,
      method,
      url: url.trim(),
      data: isEncrypted ? encryptedData : data, // Send encrypted or raw JSON based on .env
      headers: headersData,
      params,
      withCredentials: true,
    };

    // Dispatch pending state
    store.dispatch({ type: nextActionType.PENDING, payload: "" });

    // Send API request
    const response: AxiosResponse = await axios(options);

    // Ensure response decryption if necessary
    let responseData: any = response?.data;
    if (response.headers["x-encrypted"] === "true" && responseData?.encrypted) {
      responseData = decryptData(responseData.encrypted);
    }

    // Success handling
    if (successMsg && responseData?.msg) {
      store.dispatch({
        type: TOAST_MESSAGE,
        payload: { type: "success", head: "Success", text: responseData.msg },
      });
    }

    // Dispatch success state
    store.dispatch({ type: nextActionType.SUCCESS, payload: responseData });

    return responseData;
  } catch (error: any) {
    console.error("API Request Error:", error);

    let errorMessage = "Something went wrong.";
    let statusCode = 500;

    if (error.response) {
      statusCode = error.response.status;
      errorMessage = error.response.data?.msg || `Error ${statusCode}`;
    } else if (error.message) {
      errorMessage = error.message;
    }

    if (errorMsg) {
      store.dispatch({
        type: TOAST_MESSAGE,
        payload: { type: "error", head: "Error", text: errorMessage },
      });
    }
  
    store.dispatch({
      type: nextActionType.FAILURE,
      payload: {msg: errorMessage},
      status: statusCode,
    });

    return null;
  }
};
